import type { AssistantMessage } from '@ag-ui/core';
import type { CopilotChatAssistantMessageProps } from '@copilotkit/react-core/v2';
import { InkAiAssistantMessage } from '@movable/ui';

import { FEEDBACK_ISSUE_TYPES } from '@/components/shared/AgentAssistantPopup/feedbackIssueTypes';

export type InkAssistantMessageSlotProps = Omit<
  CopilotChatAssistantMessageProps,
  'onCopy' | 'onFeedback' | 'onFeedbackReasonSubmit' | 'onThumbsDown'
> & {
  onThumbsUp?: (message: AssistantMessage) => void;

  onThumbsDown?: (
    message: AssistantMessage,
    values: {
      issueType: string;
      comment: string;
    },
  ) => void;

  onCopy?: (content: string, messageIds: string[]) => void;
  messageIds?: string[];
  /** True when the message comes after the latest user message. */
  isCurrentTurn?: boolean;
};

export const InkAssistantMessageSlot = ({
  message,
  messages,
  isRunning,
  onThumbsUp,
  onThumbsDown,
  onCopy,
  messageIds,
  isCurrentTurn = false,
}: InkAssistantMessageSlotProps) => {
  const isLastMessageInTurn =
    messageIds?.[messageIds.length - 1] === message.id;
  const isLastMessage = messages?.[messages.length - 1]?.id === message.id;
  const isGenerating = Boolean(isRunning && isLastMessage);
  const isLoading = isGenerating && (message.content ?? '').trim() === '';

  // Earlier turns keep the row in the layout and only hide it visually.
  // Unmounting it while a reply streams shrinks the content and breaks the
  // scroll position set for the new user message. The turn that is streaming
  // has no row until the run ends, so no blank space is reserved above the
  // thinking indicator.
  const showActions = isLastMessageInTurn && (!isCurrentTurn || !isRunning);
  const isFeedbackVisible = isLastMessageInTurn && isCurrentTurn && !isRunning;

  return (
    <InkAiAssistantMessage
      content={message.content ?? ''}
      isGenerating={isGenerating}
      isLoading={isLoading}
      showActions={showActions}
      feedbackReasons={[...FEEDBACK_ISSUE_TYPES]}
      onFeedback={(kind) => {
        if (kind === 'up') {
          onThumbsUp?.(message);
        }
      }}
      onFeedbackReasonSubmit={(values) => {
        onThumbsDown?.(message, values);
      }}
      onCopy={() => {
        onCopy?.(message.content ?? '', messageIds ?? [message.id]);
      }}
      sx={{
        fontSize: '0.875rem',
        '& table': {
          display: 'block',
          maxWidth: '100%',
          overflowX: 'auto',
        },
        '& th, & td': {
          whiteSpace: 'nowrap',
        },
        '& [data-test-ink-ai-feedback-options]': {
          opacity: isFeedbackVisible ? 1 : 0,
          pointerEvents: isFeedbackVisible ? 'auto' : 'none',
          transition: 'opacity 150ms ease',
        },
        ...(isLastMessageInTurn && !isRunning
          ? {
              '&:hover [data-test-ink-ai-feedback-options]': {
                opacity: 1,
                pointerEvents: 'auto',
              },
            }
          : {}),
      }}
    />
  );
};
